import { cloneDeep } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import { config } from '../../config/env';
import { adminManagerRegEx, internalTenantTypeRegEx, notApprovedStatusRegEx } from '../../constants/regularExpressions';
import { checkIfEntrySubmitted } from '../../containers/Timesheet/helpers';
import { secondsToHoursFixed } from '../../helpers/time';
import { includeIntacctId, sortObjectsByNameAsc } from '../../helpers/utils';
import { ReduxState } from '../../types/redux/state';
import { ClientsStoreItem } from '../../types/redux/stores/clients.types';
import { DimensionsStoreItem } from '../../types/redux/stores/dimensions.types';
import { ProjectsStoreItem } from '../../types/redux/stores/projects.types';
import { TimesheetEntriesStore, TimesheetEntriesStoreItem } from '../../types/redux/stores/timesheetEntries.types';
import {
  filterAssignedClusters,
  filterUnAssignedClusters,
  groupAndSortClusters,
  includeIntacctIdInClusterDimensionNames,
  includeIntacctIdInEntryDimensionNames,
  rowMinWidth,
} from './helpers';

const getApprovals = (state: ReduxState) => state.approvals || {};
const getDoneApprovalsData = (state: ReduxState) => state.approvals?.done?.data || [];
const getTodoApprovalsData = (state: ReduxState) => state.approvals?.todo?.data || [];
const getClusterAssignModal = (state: ReduxState) => state.clusterAssignModal || {};
const getClusters = (state: ReduxState) => state.clusters || {};
const getClustersData = (state: ReduxState) => state.clusters?.data || [];
const getCompany = (state: ReduxState) => state.company || {};
const getCompanyData = (state: ReduxState) => state.company?.data || {};
const getGeneralData = (state: ReduxState) => state.general?.data || {};
const getTeam = (state: ReduxState) => state.team || {};
const getTimesheet = (state: ReduxState) => state.timesheet || {};
const getTimesheetInitialLoad = (state: ReduxState) => state.timesheet?.initialLoad || false;
const getTimesheetLoading = (state: ReduxState) => state.timesheet?.loading || false;
const getTimesheetSaving = (state: ReduxState) => state.timesheet?.saving || false;
const getTimesheetStartDate = (state: ReduxState) => state.timesheet?.data?.startDate || null;
const getTimesheetEndDate = (state: ReduxState) => state.timesheet?.data?.endDate || null;
const getTimesheetStatus = (state: ReduxState) => state.timesheet?.data?.status || 'NO_STATUS';
const getTimesheetEntries = (state: ReduxState) => state.timesheetEntries || {};
const getTimesheetEntriesInitialLoad = (state: ReduxState) => state.timesheetEntries?.initialLoad || false;
const getTimesheetEntriesLoading = (state: ReduxState) => state.timesheetEntries?.loading || false;
const getTimesheetEntriesSaving = (state: ReduxState) => state.timesheetEntries?.saving || false;
const getTimesheetEntriesData = (state: ReduxState) => state.timesheetEntries?.data || {};
const getTimesheetId = (state: ReduxState) => state.timesheet.data?.id;
const getTimesheetRows = (state: ReduxState) => state.timesheetRows || {};
const getTimesheetRowsInitialLoad = (state: ReduxState) => state.timesheetRows?.initialLoad || false;
const getTimesheetRowsLoading = (state: ReduxState) => state.timesheetRows?.loading || false;
const getTimesheetRowsSaving = (state: ReduxState) => state.timesheetRows?.saving || false;
const getTimesheetRowsData = (state: ReduxState) => state.timesheetRows?.data || [];
const getTimesheets = (state: ReduxState) => state.timesheets || {};
const getTimesheetsData = (state: ReduxState) => state.timesheets?.data || [];
const getApprovedTimesheets = (state: ReduxState) =>
  state.timesheets?.data.filter((t: any) => t.status === 'APPROVED').sort((a: any, b: any) => b.endDate.localeCompare(a.endDate)) || [];
const getToken = (state: ReduxState) => state.user.token || null;
const getUser = (state: ReduxState) => state.user || {};
const getUserData = (state: ReduxState) => state.user?.data || {};
const getUserNotificationSettings = (state: ReduxState) => state.userNotificationSettings || {};
const getUserSettings = (state: ReduxState) => state.userSettings || {};
const getShowIntacctId = (state: ReduxState) => state.userSettings?.data?.showIntacctId || false;
const getShowCustomer = (state: ReduxState) => state.company.data?.showCustom || false;
const getShowProject = (state: ReduxState) => state.company.data?.showProject || false;

// Dimension related
const getClientsData = (state: ReduxState) => state.clients?.data || [];
const getClientsLoading = (state: ReduxState) => state.clients?.loading || false;
const getProjectsPerClientId = (state: ReduxState) => state.projects?.perClientId || {};
const getTasksPerProjectId = (state: ReduxState) => state.tasks?.perProjectId || {};
const getDimensions = (state: ReduxState) => state.dimensions || {};
const getDimensionsData = (state: ReduxState) => state.dimensions?.data || [];
const getDimensionsInitialLoad = (state: ReduxState) => state.dimensions?.initialLoad || false;
const getDimensionsLoading = (state: ReduxState) => state.dimensions?.loading || false;
const getItemsData = (state: ReduxState) => state.items?.data || [];
const getItemsInitialLoad = (state: ReduxState) => state.items?.initialLoad || false;
const getItemsLoading = (state: ReduxState) => state.items?.loading || false;
const getTimeTypesData = (state: ReduxState) => state.timeTypes?.data || [];
const getTimeTypesInitialLoad = (state: ReduxState) => state.timeTypes?.initialLoad || false;
const getTimeTypesLoading = (state: ReduxState) => state.timeTypes?.loading || false;

// helpers
const findDepartmentDimension = (dimensionsData: DimensionsStoreItem[]) =>
  (dimensionsData || []).find((d) => d.integrationMeta?.objectName === 'DEPARTMENT');
const findLocationDimension = (dimensionsData: DimensionsStoreItem[]) =>
  (dimensionsData || []).find((d) => d.integrationMeta?.objectName === 'LOCATION');
const findCostTypeDimension = (dimensionsData: DimensionsStoreItem[]) =>
  (dimensionsData || []).find((d) => d.integrationMeta?.objectName === 'COSTTYPE');

const get_env_REACT_APP_FORCE_MFA_DISABLED = () => process.env.REACT_APP_FORCE_MFA_DISABLED === 'true';
const get_env_REACT_APP_FORCE_MFA_EXCLUDE = () => process.env.REACT_APP_FORCE_MFA_DISABLED;

export const selectIsAuthenticated = createSelector(getToken, (token) => token !== null);
export const selectToken = createSelector(getToken, (token) => token);

export const selectShouldRefreshData = createSelector(getCompany, getToken, (company, token) => {
  if (!token) return false;
  if (!company.lastSync || !moment(company.lastSync).isValid()) return true;
  return moment().diff(moment(company.lastSync), 'minutes') > config.REACT_APP_MINUTES_TO_REFRESH_CPTI;
});

export const selectDisableMFASwitch = createSelector(
  getUserData,
  getCompanyData,
  get_env_REACT_APP_FORCE_MFA_DISABLED,
  get_env_REACT_APP_FORCE_MFA_EXCLUDE,
  (userData, companyData, forceMFADisabled, forceMFAExclude) => {
    if (forceMFADisabled) return true;
    if (companyData.tenantType === 'debug') return true;
    if (forceMFAExclude) {
      const intacctCompanyId = userData?.company?.intacctCompanyId;
      if (!intacctCompanyId) {
        return true;
      }
      const excludeCompanies = forceMFAExclude.split(',');
      return excludeCompanies.some((c) => {
        const regex = new RegExp(`^${c}$`, 'i');
        return intacctCompanyId && regex.test(intacctCompanyId);
      });
    }
    return false;
  }
);

export const selectNeedsToSetUpMfa = createSelector(
  getUserData,
  getCompanyData,
  (userData, companyData) => !!companyData.mfaRequired && !userData?.twoFactorEnabled
);

export const selectAllDimensionsInitialLoad = createSelector(
  getCompany,
  getItemsInitialLoad,
  getItemsLoading,
  getTimeTypesInitialLoad,
  getTimeTypesLoading,
  getDimensionsInitialLoad,
  getDimensionsLoading,
  (company, itemsInitialLoad, itemsLoading, timeTypesInitialLoad, timeTypesLoading, dimensionsInitialLoad, dimensionsLoading) => {
    if (company.loading) return true;

    return company.data.showItem && !itemsLoading
      ? itemsInitialLoad
      : true && company.data.showTimeType && !timeTypesLoading
      ? timeTypesInitialLoad
      : true && company.data.showDepartmentLocation && !dimensionsLoading
      ? dimensionsInitialLoad
      : true;
  }
);

export const selectTimesheetLoaderActive = createSelector(
  getTimesheetLoading,
  getTimesheetSaving,
  getTimesheetRowsLoading,
  getTimesheetRowsSaving,
  getTimesheetEntriesLoading,
  getTimesheetEntriesSaving,
  (timesheetLoading, timesheetSaving, rowsLoading, rowsSaving, entriesLoading, entriesSaving) =>
    timesheetLoading || timesheetSaving || rowsLoading || rowsSaving || entriesLoading || entriesSaving
);

export const selectUser = createSelector(
  getUser, // user
  (user) => user
);
export const selectUserData = createSelector(
  getUserData, // user
  (data) => data
);
export const isAdminOrManagerSelector = createSelector(getUserData, (user) => adminManagerRegEx.test(user.role));
export const selectUserId = createSelector(getUserData, (user) => user.id);

/*
 *     Timesheets
 */
export const selectTimesheets = createSelector(getTimesheets, (timesheets) => timesheets);
export const selectActiveTimesheets = createSelector(getTimesheetsData, getApprovedTimesheets, (timesheets, lastApprovedTimesheets) =>
  timesheets.filter((timesheet: any) => {
    if (notApprovedStatusRegEx.test(timesheet.status)) {
      return true;
    }
    if (timesheet.status === 'APPROVED') {
      return lastApprovedTimesheets[0]?.id === timesheet.id;
    }
    return false;
  })
);

/**
 *     Timesheet
 */
export const selectTimesheet = createSelector(getTimesheet, (timesheet) => timesheet);
export const selectTimesheetId = createSelector(selectTimesheet, (timesheet) => timesheet?.data?.id);
export const selectTimesheetStartDate = createSelector(getTimesheetStartDate, (startDate) =>
  startDate ? startDate.substring(0, 10) : null
);
export const selectTimesheetEndDate = createSelector(getTimesheetEndDate, (endDate) => (endDate ? endDate.substring(0, 10) : null));
export const selectTimesheetComponentInitialLoad = createSelector(
  getTimesheetInitialLoad,
  getTimesheetEntriesInitialLoad,
  getTimesheetRowsInitialLoad,
  (timesheetInitialLoad, timesheetEntriesInitialLoad, timesheetRowsInitialLoad) =>
    !!timesheetInitialLoad && !!timesheetEntriesInitialLoad && !!timesheetRowsInitialLoad
);
export const selectSubmitButtonDisabled = createSelector(
  getTimesheetStatus,
  getTimesheetEntriesData,
  (timesheetStatus, entriesData) => /^(APPROVED|INVOICED)$/.test(timesheetStatus) || !(Object.keys(entriesData)?.length > 0)
);

/**
 *     Timesheet Rows
 */
export const selectTimesheetRows = createSelector(getTimesheetRows, (rows) => rows);
export const selectTimesheetRowsData = createSelector(getTimesheetRowsData, getShowIntacctId, (rows: any[], showIntacctId) => {
  let _rows = cloneDeep(rows);
  if (showIntacctId) {
    _rows = _rows.map((row: any) => {
      if (row.dimensionValues) {
        const client = row.dimensionValues?.client;
        if (client) {
          row.dimensionValues.client = includeIntacctId(client);
        }
        const project = row.dimensionValues?.project;
        if (project) {
          row.dimensionValues.project = includeIntacctId(project);
        }
        const task = row.dimensionValues?.task;
        if (task) {
          row.dimensionValues.task = includeIntacctId(task);
        }
      }
      return row;
    });
  }
  return _rows.sort((a, b) => {
    const createdAtCheck = b.created_at.localeCompare(a.created_at);
    if (createdAtCheck === 0) {
      return b.id.localeCompare(a.id);
    }
    return createdAtCheck;
  });
});
export const selectTimesheetRowsWithNegativeHours = createSelector(selectTimesheetRowsData, (rowsData) =>
  rowsData.filter((row: any) => !!row.negativeHours)
);
export const selectTimesheetRowsWithPositiveHours = createSelector(selectTimesheetRowsData, (rowsData) =>
  rowsData.filter((row: any) => !row.negativeHours)
);
export const selectRowFirstEntry = createSelector(
  (state: ReduxState, row?: any) => (row?.entries?.length > 0 ? row?.entries[0]?.id : null),
  getTimesheetEntriesData,
  getShowIntacctId,
  (firstRowEntryId, entriesData, showIntacctId) => {
    if (!firstRowEntryId || !entriesData[firstRowEntryId]) return null;
    if (!showIntacctId) return entriesData[firstRowEntryId];
    return includeIntacctIdInEntryDimensionNames(entriesData[firstRowEntryId]);
  }
);
export const selectRowHasEntries = createSelector(
  (state: ReduxState, row: any) => row?.entries || [],
  (entries) => entries?.length > 0
);
export const selectRowHasSubmittedEntries = createSelector(
  (state: ReduxState, row?: any) => row?.entries || [],
  (entries: TimesheetEntriesStoreItem[]) => entries?.some((e) => checkIfEntrySubmitted(e))
);
export const selectRowClusters = createSelector(
  getClustersData,
  (state: ReduxState, row: any) => row,
  (clustersData: any[], row) =>
    clustersData?.filter(
      (c) => c.timesheet_entry_id && (row.entries || []).some((e: any) => e.id === c.timesheet_entry_id && !c.miscellaneousReason)
    )
);
export const selectIsLastRowWithEntries = createSelector(
  getTimesheetRowsData,
  (rows: any[]) => rows.filter((r) => r.entries?.length > 0).length === 1
);
export const selectIsLastRow = createSelector(getTimesheetRowsData, (rows) => rows?.length === 1);
export const selectDeleteRowDisabled = createSelector(
  (state: ReduxState, row: any) => row,
  (row: any) => row?.entries?.some((e: TimesheetEntriesStoreItem) => checkIfEntrySubmitted(e))
);

/**
 *     Entries
 */
export const selectTimesheetEntries = createSelector(getTimesheetEntries, (entries) => entries);
export const selectTimesheetEntriesData = createSelector(getTimesheetEntriesData, getShowIntacctId, (entries, showIntacctId) => {
  if (!showIntacctId) return entries;
  const entriesCopy: Record<string, any> = cloneDeep(entries);
  for (const entryId of Object.keys(entriesCopy)) {
    entriesCopy[entryId] = includeIntacctIdInEntryDimensionNames(entriesCopy[entryId]);
  }
  return entriesCopy;
});
export const selectDisabledEntries = createSelector(getTimesheetEntries, (entries) => entries.disabledEntries);
export const selectNotAllEntriesSubmitted = createSelector(getTimesheetEntriesData, (entriesData) =>
  Object.values(entriesData).some((e) => {
    return e ? /^(DRAFT|DECLINED)$/.test(e.status) : false;
  })
);
export const selectEntryById = createSelector(
  getTimesheetEntriesData,
  getShowIntacctId,
  (state: ReduxState, entryId: TimesheetEntriesStoreItem['id']) => entryId,
  (entriesData, showIntacctId, entryId) => {
    if (!entryId || !entriesData[entryId]) return null;
    if (!showIntacctId) return entriesData[entryId];
    return includeIntacctIdInEntryDimensionNames(entriesData[entryId]);
  }
);
export const selectEntriesDateTotal = createSelector(
  (state: ReduxState, day: moment.Moment) => day,
  getTimesheetEntriesData,
  (day, entriesData) => {
    return secondsToHoursFixed(
      Object.values(entriesData).reduce((acc, cur) => {
        if (moment(cur.date, 'YYYY-MM-DD').isSame(day.format('YYYY-MM-DD'))) {
          return acc + parseInt(`${cur.duration || 0}`, 10);
        }
        return acc;
      }, 0)
    );
  }
);

/**
 *     General
 */
export const selectGeneralShowApprovalsTab = createSelector(getGeneralData, (generalData) => generalData?.hasApproval || false);
export const selectHideBrowserWarning = createSelector(getGeneralData, (generalData) => generalData?.hideBrowserWarning || false);

/**
 *     Cluster Assign modal
 */
export const selectClusterAssignModal = createSelector(getClusterAssignModal, (cam) => cam);
export const selectShouldDisplayClusterAssignModal = createSelector(getClusterAssignModal, (cam) => !!cam.visible);
export const selectClusterOfClusterAssignModal = createSelector(
  getClusterAssignModal,
  getClustersData,
  (clusterAssignModal, clustersData: any[]) =>
    clustersData.find((c) => c.id === clusterAssignModal.cluster.id) || clusterAssignModal.cluster
);
export const selectEntryOfClusterAssignModal = createSelector(
  getClusterAssignModal,
  getClustersData,
  getTimesheetEntriesData,
  (clusterAssignModal, clustersData: any[], entriesData: TimesheetEntriesStore['data']) => {
    const entryId = (clustersData?.find((c) => c.id === clusterAssignModal.cluster.id) || clusterAssignModal.cluster)?.timesheet_entry_id;
    if (!entryId) return null;
    return entriesData[entryId] || null;
  }
);

/**
 *     Clients
 */
export const selectClientsData = createSelector(getClientsData, (clientsData) => clientsData);
export const selectClientsLoading = createSelector(getClientsLoading, (clientsLoading) => clientsLoading);

/**
 *     Projects
 */
export const selectProjectsPerClientId = createSelector(
  getProjectsPerClientId,
  getShowCustomer,
  (state: ReduxState, clientId: ClientsStoreItem['id']) => clientId,
  (perClientId, showCustomer, clientId) => {
    if (!showCustomer) return perClientId['all']?.data || [];
    if (!perClientId || !clientId) return [];
    return perClientId[clientId]?.data || [];
  }
);
export const selectProjectsPerClientIdOfRow = createSelector(
  getProjectsPerClientId,
  getShowCustomer,
  (state: ReduxState, row: any) => row,
  (perClientId, showCustomer, row) => {
    if (!showCustomer) return perClientId['all']?.data || [];
    if (!row || !perClientId) return [];
    const clientId = row.dimensionValues?.clientId;
    return perClientId[clientId]?.data || [];
  }
);
export const selectProjectsPerClientIdOfClusterAssignModal = createSelector(
  getProjectsPerClientId,
  getShowCustomer,
  getClusterAssignModal,
  (perClientId, showCustomer, clusterAssignModal) => {
    if (!showCustomer) return perClientId['all']?.data || [];
    if (!perClientId || !clusterAssignModal?.selectedValues) return [];
    const clientId = clusterAssignModal.selectedValues?.clientId;
    return clientId ? perClientId[clientId]?.data : [];
  }
);

/**
 *     Tasks
 */
export const selectTasksPerProjectId = createSelector(
  getTasksPerProjectId,
  getShowProject,
  (state: ReduxState, projectId: ProjectsStoreItem['id']) => projectId,
  (perProjectId, showProject, projectId) => {
    if (!projectId || !perProjectId) return [];
    if (!showProject) return perProjectId['all']?.data || [];
    return perProjectId[projectId]?.data || [];
  }
);
export const selectTasksPerProjectIdOfRow = createSelector(
  getTasksPerProjectId,
  getShowProject,
  (state: ReduxState, row: any) => row,
  (perProjectId, showProject, row) => {
    if (!showProject) return perProjectId['all']?.data || [];
    if (!row || !perProjectId) return [];
    const projectId = row.dimensionValues?.projectId;
    return perProjectId[projectId]?.data || [];
  }
);
export const selectTasksPerProjectIdOfClusterAssignModal = createSelector(
  getTasksPerProjectId,
  getShowProject,
  getClusterAssignModal,
  (perProjectId, showProject, clusterAssignModal) => {
    if (!showProject) return perProjectId['all']?.data || [];
    if (!clusterAssignModal?.selectedValues || !perProjectId) return [];
    const projectId = clusterAssignModal.selectedValues?.projectId;
    return projectId ? perProjectId[projectId]?.data || [] : [];
  }
);

/**
 *     Dimensions
 */
export const selectTimeTypesData = createSelector(getTimeTypesData, (timeTypesData) => timeTypesData);
export const selectItemsData = createSelector(getItemsData, (itemsData) => itemsData);
export const selectDimensions = createSelector(getDimensions, (dimensions) => dimensions);
export const selectDimensionsData = createSelector(getDimensionsData, getShowIntacctId, (dimensionsData, showIntacctId) => {
  if (!showIntacctId) return dimensionsData;
  return cloneDeep(dimensionsData)
    .map((d) => {
      d.values = d.values?.map(includeIntacctId);
      return d;
    })
    .sort((a, b) => sortObjectsByNameAsc(a, b));
});
export const selectDepartmentDimension = createSelector(selectDimensionsData, findDepartmentDimension);
export const selectLocationDimension = createSelector(selectDimensionsData, findLocationDimension);
export const selectDepartmentOptions = createSelector(selectDimensionsData, (dimensionsData) =>
  (findDepartmentDimension(dimensionsData)?.values || []).sort((a, b) => sortObjectsByNameAsc(a, b))
);
export const selectLocationOptions = createSelector(selectDimensionsData, (dimensionsData) =>
  (findLocationDimension(dimensionsData)?.values || []).sort((a, b) => sortObjectsByNameAsc(a, b))
);
export const selectCostTypeOptions = createSelector(selectDimensionsData, (dimensionsData) =>
  (findCostTypeDimension(dimensionsData)?.values || []).sort((a, b) => sortObjectsByNameAsc(a, b))
);
export const selectDepartmentDisplayName = createSelector(
  getDimensionsData,
  (dimensionsData) => findDepartmentDimension(dimensionsData)?.name || []
);
export const selectLocationDisplayName = createSelector(
  getDimensionsData,
  (dimensionsData) => findLocationDimension(dimensionsData)?.name || []
);

/*
 *     Approvals DONE
 */
export const selectDoneApprovals = createSelector(getApprovals, (approvals) => approvals.done.data);
export const selectDoneApprovalsByEmployee = createSelector(getApprovals, (approvals) => approvals.doneByEmployee.data);
export const selectDoneApprovalsByPeriod = createSelector(getApprovals, (approvals) => approvals.doneByPeriod.data);
export const selectDoneApprovalsByProject = createSelector(getApprovals, (approvals) => approvals.doneByProject.data);
export const selectDoneApprovalsByClient = createSelector(getApprovals, (approvals) => approvals.doneByClient.data);
export const selectDoneApprovalTimesheet = createSelector(
  getDoneApprovalsData,
  (state: ReduxState, timesheetId: string) => timesheetId,
  (doneData: any[], timesheetId) => doneData.find((d) => d.timesheet_id === timesheetId)
);

/*
 *     Approvals TODO
 */
export const selectTodoApprovals = createSelector(getApprovals, (approvals) => approvals.todo.data);
export const selectTodoApprovalsByEmployee = createSelector(getApprovals, (approvals) => approvals.todoByEmployee.data);
export const selectTodoApprovalsByPeriod = createSelector(getApprovals, (approvals) => approvals.todoByPeriod.data);
export const selectTodoApprovalsByProject = createSelector(getApprovals, (approvals) => approvals.todoByProject.data);
export const selectTodoApprovalTimesheet = createSelector(
  getTodoApprovalsData,
  (state: ReduxState, timesheetId: string) => timesheetId,
  (todoData: any[], timesheetId) => todoData.find((d) => d.timesheet_id === timesheetId)
);
export const selectSelectedTodoTimesheets = createSelector(
  getTodoApprovalsData,
  (state: ReduxState, selected: string[]) => selected,
  (todoData: any[], selected) => todoData.filter((d) => selected.indexOf(d.timesheet_id) > -1)
);

/**
 *     User Notification Settings
 */
export const selectUserNotificationSettingsInitialLoad = createSelector(getUserNotificationSettings, (settings) => settings.initialLoad);

/**
 *     Company
 */
export const selectCompany = createSelector(getCompany, (company) => company);
export const selectCompanyData = createSelector(getCompanyData, (companyData) => companyData);
export const selectCompanyEntities = createSelector(getCompanyData, (companyData) => companyData?.entities);
export const selectCompanyId = createSelector(getCompany, (companyData) => companyData?.id);
export const selectCompanyLastSync = createSelector(getCompany, (company) => company.lastSync);
export const selectCompanyTenantType = createSelector(getCompanyData, (companyData) => companyData?.tenantType);
export const selectIsInternalTenant = createSelector(getCompanyData, (companyData) =>
  internalTenantTypeRegEx.test(companyData?.tenantType || 'NO_TENANT_TYPE')
);
export const selectShowCustomer = createSelector(getCompanyData, (companyData) => !!companyData?.showCustom);
export const selectShowProject = createSelector(getCompanyData, (companyData) => !!companyData?.showProject);
export const selectShowTask = createSelector(getCompanyData, (companyData) => !!companyData?.showTask);
export const selectShowItem = createSelector(getCompanyData, (companyData) => !!companyData?.showItem);
export const selectShowTimeType = createSelector(getCompanyData, (companyData) => !!companyData?.showTimeType);
export const selectShowDepartmentLocation = createSelector(getCompanyData, (companyData) => !!companyData?.showDepartmentLocation);
export const selectShowBillable = createSelector(getCompanyData, (companyData) => !!companyData?.showBillable);
export const selectShowDescriptionFirst = createSelector(getCompanyData, (companyData) => !!companyData?.showDescriptionFirst);
export const selectValidateProjectDates = createSelector(getCompanyData, (companyData) => companyData?.validateProjectDates);
export const selectClientDisplayName = createSelector(getCompanyData, (companyData) => companyData?.clientDisplayName);
export const selectProjectDisplayName = createSelector(getCompanyData, (companyData) => companyData?.projectDisplayName);
export const selectTaskDisplayName = createSelector(getCompanyData, (companyData) => companyData?.taskDisplayName);
export const selectItemDisplayName = createSelector(getCompanyData, (companyData) => companyData?.itemDisplayName);
export const selectRowMinWidth = createSelector(getCompanyData, rowMinWidth);

/**
 *     User
 */
export const selectIsAdmin = createSelector(getUserData, (userData) => userData?.role === 'admin' || false);
export const selectIsAdminManager = createSelector(getUserData, (userData) => adminManagerRegEx.test(userData?.role || 'NO_USER_ROLE'));

/**
 *     Settings
 */
export const selectSettingsLoading = createSelector(
  getCompany,
  getTeam,
  getUserNotificationSettings,
  getUserSettings,
  (company, team, notifSettings, userSettings) => company.loading || team.loading || notifSettings.loading || userSettings.loading
);
export const selectUserSettings = createSelector(getUserSettings, (userSettings) => userSettings);
export const selectDefaultShowWeekends = createSelector(
  getUserSettings,
  (userSettings) => userSettings?.data?.defaultShowWeekends || false
);
export const selectUserSettingsMergeRows = createSelector(getUserSettings, (userSettings) => userSettings.data?.mergeRows);
export const selectShowIntacctId = createSelector(getShowIntacctId, (showIntacctId) => !!showIntacctId);

/**
 *     Clusters
 */
export const selectClusters = createSelector(getClusters, (clusters) => clusters);
export const selectClusterEntry = createSelector(
  getTimesheetEntriesData,
  getUserSettings,
  getShowIntacctId,
  (state: ReduxState, cluster: any) => cluster.timesheet_entry_id,
  (entriesData, showIntacctId, clusterEntryId) => {
    if (!clusterEntryId || !entriesData[clusterEntryId]) return null;
    if (!showIntacctId) return entriesData[clusterEntryId];
    return {
      ...entriesData[clusterEntryId],
      client: includeIntacctId(entriesData[clusterEntryId]),
      project: includeIntacctId(entriesData[clusterEntryId]),
      task: includeIntacctId(entriesData[clusterEntryId]),
      item: includeIntacctId(entriesData[clusterEntryId]),
    };
  }
);
export const selectAssignedClusters = createSelector(
  getClustersData,
  getTimesheetEntriesData,
  getTimesheetId,
  getShowIntacctId,
  (clustersData: any[], entries, timesheetId, showIntacctId) => {
    if (!showIntacctId) return groupAndSortClusters(filterAssignedClusters(clustersData, entries, timesheetId));
    const updatedClusterData = clustersData.map((c) => includeIntacctIdInClusterDimensionNames(c));
    return groupAndSortClusters(filterAssignedClusters(updatedClusterData, entries, timesheetId));
  }
);
export const selectUnAssignedClusters = createSelector(getClustersData, getShowIntacctId, (clustersData: any[], showIntacctId) => {
  if (!showIntacctId) return groupAndSortClusters(filterUnAssignedClusters(clustersData));
  const updatedClusterData = clustersData.map((c) => includeIntacctIdInClusterDimensionNames(c));
  return groupAndSortClusters(filterUnAssignedClusters(updatedClusterData));
});
